import React, { useEffect, useMemo, useRef, useState } from 'react';

export type SwitcherUnit = { id: string; unitNumber: string; status: string; buyer: string };

const TONE: Record<string, string> = { Booked: 'acc', Hold: 'warn', Available: 'mute' };

/** Title button "G-2 ▾" that opens a search list of the project's units. `/` opens it from the page. */
export function UnitSwitcher({ current, units, onPick, openSignal, onOpen }: { current: string; units: SwitcherUnit[]; onPick: (id: string) => void; openSignal: number; onOpen?: () => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (!open) return;
    onOpen?.();
    setQ('');
    setActive(0);
    window.setTimeout(() => input.current?.focus(), 0);
    const onDown = (e: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase().replace(/\s+/g, '');
    if (!needle) return units;
    return units
      .map((u) => {
        const no = u.unitNumber.toLowerCase().replace(/\s+/g, '');
        const buyer = u.buyer.toLowerCase().replace(/\s+/g, '');
        const rank = no.startsWith(needle) ? 0 : no.includes(needle) ? 1 : buyer.includes(needle) ? 2 : -1;
        return { u, rank };
      })
      .filter((x) => x.rank >= 0)
      .sort((a, b) => a.rank - b.rank)
      .map((x) => x.u);
  }, [q, units]);

  useEffect(() => {
    (list.current?.children[active] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (id: string) => {
    setOpen(false);
    if (id) onPick(id);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(hits.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hits[active]) pick(hits[active].id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="ou-switch" ref={wrap}>
      <button className="ou-switchbtn" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open} title="Switch unit ( / )">
        <h1>{current}</h1>
        <svg className="ot-ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open ? (
        <div className="ou-switchpop" role="dialog" aria-label="Switch unit">
          <input
            ref={input}
            className="ou-switchin"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
            placeholder="Unit number or buyer name"
            aria-label="Search units"
            role="combobox"
            aria-expanded="true"
            aria-controls="ou-switch-list"
            aria-activedescendant={hits[active] ? `ou-sw-${hits[active].id}` : undefined}
          />
          <div className="ou-switchlist" id="ou-switch-list" role="listbox" ref={list}>
            {hits.length ? (
              hits.map((u, i) => (
                <button
                  key={u.id}
                  id={`ou-sw-${u.id}`}
                  role="option"
                  aria-selected={i === active}
                  className={`ou-switchrow${i === active ? ' on' : ''}${u.unitNumber === current ? ' cur' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(u.id)}
                >
                  <span className="mono">{u.unitNumber}</span>
                  <span className="b">{u.buyer}</span>
                  <span className={`ot-chip c-${TONE[u.status] || 'mute'}`}>{u.status}</span>
                </button>
              ))
            ) : (
              <p className="faint" style={{ margin: 0, padding: '10px 12px', fontSize: 13 }}>No unit matches "{q}".</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
